import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Lock, Unlock, MessageSquare, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguageNavigation } from '../../routing/LanguageWrapper';
import { ROLES } from '../../../utils/rbac';
import './WorkspaceHeader.css';

const STATUS_COLORS = {
  draft: { bg: 'rgba(138, 122, 74, 0.12)', color: '#8A7A4A' },
  in_progress: { bg: 'rgba(74, 90, 58, 0.12)', color: '#4A5A3A' },
  needs_review: { bg: 'rgba(138, 122, 74, 0.12)', color: '#8A7A4A' },
  completed: { bg: 'rgba(90, 122, 58, 0.12)', color: '#5A7A3A' },
  locked: { bg: 'rgba(74, 74, 74, 0.12)', color: '#4A4A4A' },
};

const STATUS_LABELS = {
  draft: 'Draft',
  in_progress: 'In Progress',
  needs_review: 'Needs Review',
  completed: 'Completed',
  locked: 'Locked',
};

export function WorkspaceHeader({ report, onSave, onLockToggle, onOpenComments, onOpenActivityLog, saving }) {
  const { user } = useAuth();
  const { getLocalizedPath } = useLanguageNavigation();
  const { t } = useTranslation();
  
  const canSave = [ROLES.ADMIN, ROLES.REVIEWER].includes(user?.role);
  const canLock = user?.role === ROLES.ADMIN || user?.role === ROLES.SUPERADMIN;
  
  const statusStyle = STATUS_COLORS[report?.status] || STATUS_COLORS.draft;
  const statusLabel = t(`reports.status.${report?.status || 'draft'}`, { defaultValue: report?.status || t('reports.status.draft') });
  
  const userName = user ? `${user.firstName} ${user.lastName}` : t('reports.workspace.fallback.user');

  return (
    <div className="workspace-header">
      <div className="workspace-header__container">
        <div className="workspace-header__left">
          <Link to={getLocalizedPath('/reports')} className="workspace-header__back-link">
            <ArrowLeft size={14} />
            <span>{t('reports.title')}</span>
          </Link>
          <div className="workspace-header__title-group">
            <h1 className="workspace-header__title">{report?.name || t('reports.workspace.fallback.report')}</h1>
            <span
              className="workspace-header__status-badge"
              style={{
                background: statusStyle.bg,
                color: statusStyle.color,
              }}
            >
              {statusLabel}
            </span>
          </div>
          <span className="workspace-header__user-info">
            {t('reports.workspace.editingAs', { name: userName })}
          </span>
        </div>
        
        <div className="workspace-header__actions">
          {onOpenComments && (
            <Button
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Comments button clicked');
                onOpenComments();
              }}
              className="workspace-header__drawer-btn"
              icon={<MessageSquare size={16} />}
            >
              {t('reports.workspace.actions.comments')}
            </Button>
          )}
          
          {onOpenActivityLog && (
            <Button
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Activity button clicked');
                onOpenActivityLog();
              }}
              className="workspace-header__drawer-btn"
              icon={<Clock size={16} />}
            >
              {t('reports.workspace.actions.activity')}
            </Button>
          )}
          
          {canSave && (
            <Button
              variant="secondary"
              onClick={onSave}
              disabled={saving}
              className="workspace-header__save-btn"
              icon={<Save size={16} />}
            >
              {t('common.save')}
            </Button>
          )}
          
          {canLock && (
            <Button
              variant="ghost"
              onClick={onLockToggle}
              className="workspace-header__lock-btn"
              icon={report?.locked ? <Unlock size={16} /> : <Lock size={16} />}
            >
              {report?.locked ? t('reports.workspace.actions.unlock') : t('reports.workspace.actions.lock')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

